/*
  TGP 2026-10-20. Lightweight videos for the homepage hero and the product-page video reel.

  <sisu-video>  Background video (slideshow). The <video> starts with only its poster image and
                preload="none"; it gets a source only after the page has loaded and the element is
                on screen, and pauses when it leaves the screen.
  <video-reel>  Creator video cards (replaces the Platter video testimonial slider). Nothing
                downloads until the reel is on screen after load; then the most visible card
                plays muted. Labelled play/pause and sound buttons on every card; previous/next buttons
                scroll the reel for mouse users.

  Source choice: Shopify's adaptive HLS stream where the browser plays it natively (iPhone,
  Safari), which starts small and steps up with the connection. Otherwise the smallest MP4 that
  fits (themes only get the 1080p MP4 today); if the stream fails, the MP4 takes over. On 2G/3G an MP4 plays only when the visitor taps
  play. Data Saver or reduced motion: no autoplay; the poster stays and reel cards play on tap.
*/
(() => {
  const connection = navigator.connection || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 749px)');
  const noAutoplay = () => connection.saveData === true || reduceMotion.matches;
  const slowNetwork = () => ['slow-2g', '2g', '3g'].includes(connection.effectiveType);

  const afterLoad = new Promise((resolve) => {
    const idle = () =>
      'requestIdleCallback' in window ? requestIdleCallback(resolve, { timeout: 2000 }) : setTimeout(resolve, 200);
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  });

  // The hero starts when the page has loaded (without the idle wait the reel uses), or 5 s after its poster has
  // loaded if the page load drags on: on phones on cellular the full load (app scripts, images) could take so long
  // that the poster sat there without playing (Pete, 10/20). Starting earlier than the load competed with the page:
  // a video fetching before the load event also holds that event back until its first frame (lab, 4G, 10/21).
  function heroStart(video) {
    return new Promise((resolve) => {
      if (document.readyState === 'complete') resolve();
      else window.addEventListener('load', () => resolve(), { once: true });
      const later = () => setTimeout(resolve, 5000);
      const src = video.getAttribute('poster');
      if (!src) return later();
      const img = new Image();
      img.onload = img.onerror = later;
      img.src = src;
    });
  }

  // The slideshow moves on after its delay (4.9 s) whether or not the hero video has started, so on a slow connection
  // visitors saw only the poster and then slide 2 (Pete, 10/20: "Image shows but not playing"). Hold the carousel on
  // the first slide until its video plays, then let the normal delay run. Never longer than 12 s.
  function holdCarouselFor(el, video) {
    const swiperEl = el.closest('.slideshow__swiper');
    if (!swiperEl || el.closest('.swiper-slide-duplicate')) return;
    let tries = 0;
    const hold = () => {
      const swiper = swiperEl.swiper;
      if (!swiper) {
        if (tries++ < 50) setTimeout(hold, 100); // the slideshow builds its carousel on DOMContentLoaded
        return;
      }
      if (!swiper.autoplay || !swiper.autoplay.running) return;
      const active = swiper.slides && swiper.slides[swiper.activeIndex];
      if (!active || !active.contains(el)) return;
      swiper.autoplay.stop();
      let done = false;
      const resume = () => {
        if (done) return;
        done = true;
        clearTimeout(cap);
        if (!swiper.destroyed) swiper.autoplay.start();
      };
      const cap = setTimeout(resume, 12000);
      video.addEventListener('playing', resume, { once: true });
    };
    setTimeout(hold);
  }

  // Shopify's stream playlist lists 1080p first, and Safari starts on the first entry, so an iPhone on cellular began
  // with 3 MB chunks. On Apple phones, play the 720p stream directly (about 2 MB per 3-second chunk). Other browsers
  // pick the starting quality from the connection speed, so they keep the full playlist. Any failure keeps it too.
  const applePhone = () => phone.matches && /Apple/.test(navigator.vendor || '');
  const phoneStreams = new Map();
  function phoneStream(url) {
    if (!applePhone()) return Promise.resolve(url);
    if (!phoneStreams.has(url)) {
      phoneStreams.set(
        url,
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((text) => {
            const lines = text.split(/\r?\n/);
            let best = null;
            lines.forEach((line, i) => {
              const res = /RESOLUTION=(\d+)x(\d+)/.exec(line);
              const uri = (lines[i + 1] || '').trim();
              if (!res || !uri || uri.startsWith('#')) return;
              const h = Math.min(+res[1], +res[2]);
              if (h <= 720 && (!best || h > best.h)) best = { h, uri };
            });
            return best ? new URL(best.uri, url).href : url;
          })
          .catch(() => url)
      );
    }
    return phoneStreams.get(url);
  }

  function readSources(el) {
    try {
      return JSON.parse(el.dataset.sources || '[]').filter((s) => s && s.url);
    } catch (e) {
      return [];
    }
  }

  function pickMp4(sources, video) {
    const mp4 = sources.filter((s) => s.type === 'mp4').sort((a, b) => a.h - b.h);
    if (!mp4.length) return null;
    const capped = mp4.filter((s) => s.h <= (phone.matches ? 720 : Infinity));
    const choices = capped.length ? capped : [mp4[0]];
    const target = (video.clientHeight || 400) * Math.min(window.devicePixelRatio || 1, 2) * 0.85;
    return (choices.find((s) => s.h >= target) || choices[choices.length - 1]).url;
  }

  function pickSource(sources, video, userInitiated) {
    const hls = sources.find((s) => s.type === 'hls');
    if (hls && video.canPlayType('application/vnd.apple.mpegurl')) return hls.url;
    if (slowNetwork() && !userInitiated) return null;
    return pickMp4(sources, video);
  }

  function playQuietly(video) {
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  }

  // Give the video a source; if the HLS stream fails, fall back to the MP4 once and keep playing.
  function setSource(video, sources, userInitiated) {
    const url = pickSource(sources, video, userInitiated);
    if (!url) return false;
    if (/\.m3u8(\?|$)/.test(url)) {
      video.addEventListener(
        'error',
        () => {
          const mp4 = pickMp4(sources, video);
          if (!mp4) return;
          video.src = mp4;
          playQuietly(video);
        },
        { once: true }
      );
    }
    video.src = url;
    return true;
  }

  // The slideshow's loop mode shows a copy of the first slide and swaps it for the original mid-transition. Copies
  // of one video start and pause separately, so the swap cut to a different frame (a "twitch", 10/20). Before a
  // copy plays, move it to where its twin is: the playing one, else the furthest along.
  function twinTime(el) {
    const twins = [...document.querySelectorAll('sisu-video')]
      .filter((other) => other !== el && other.dataset.sources === el.dataset.sources)
      .map((other) => other.querySelector('video'))
      .filter((v) => v && v.getAttribute('src') && v.currentTime > 0);
    const playing = twins.find((v) => !v.paused);
    if (playing) return playing.currentTime;
    return twins.length ? Math.max(...twins.map((v) => v.currentTime)) : null;
  }

  function seekTo(video, time) {
    if (time == null || Math.abs(video.currentTime - time) < 0.3) return;
    const go = () => {
      try {
        video.currentTime = time;
      } catch (e) {}
    };
    if (video.readyState >= 1) go();
    else video.addEventListener('loadedmetadata', go, { once: true });
  }

  if (!customElements.get('sisu-video')) {
    class SisuVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video || noAutoplay()) return;
        this.sources = readSources(this);
        holdCarouselFor(this, this.video);
        const hls = this.sources.find((s) => s.type === 'hls');
        Promise.all([heroStart(this.video), hls ? phoneStream(hls.url) : null]).then(([, stream]) => {
          if (hls && stream) hls.url = stream;
          if (!this.isConnected || this.observer) return;
          this.observer = new IntersectionObserver(
            (entries) => entries.forEach((entry) => this.toggle(entry.isIntersecting)),
            { threshold: 0.25 }
          );
          this.observer.observe(this);
        });
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
      }

      toggle(visible) {
        const video = this.video;
        if (!visible) {
          if (!video.paused) video.pause();
          return;
        }
        const time = twinTime(this);
        if (!video.getAttribute('src') && !setSource(video, this.sources, false)) return;
        seekTo(video, time);
        playQuietly(video);
      }
    }
    customElements.define('sisu-video', SisuVideo);
  }

  if (!customElements.get('video-reel')) {
    class VideoReel extends HTMLElement {
      connectedCallback() {
        if (!this.bound) this.bind();
        afterLoad.then(() => {
          if (!this.isConnected) return;
          this.disconnectedCallback();
          this.sectionObserver = new IntersectionObserver(
            (entries) => {
              this.inView = entries[entries.length - 1].isIntersecting;
              this.autoplay();
            },
            { threshold: 0.3 }
          );
          this.sectionObserver.observe(this);
          this.cardObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => this.visibility.set(entry.target, entry.intersectionRatio));
              this.autoplay();
            },
            { root: this.scroller, threshold: [0, 0.6, 1] }
          );
          this.cards.forEach((card) => this.cardObserver.observe(card));
        });
      }

      // Listeners are attached once per element (the theme editor can move a section, which reconnects it).
      bind() {
        this.bound = true;
        this.scroller = this.querySelector('[data-reel-scroller]');
        this.cards = Array.from(this.querySelectorAll('[data-reel-card]'));
        this.progress = this.querySelector('[data-reel-progress]');
        this.nav = this.querySelector('[data-reel-nav]');
        this.prev = this.querySelector('[data-reel-prev]');
        this.next = this.querySelector('[data-reel-next]');
        this.visibility = new Map();
        this.userChoice = null;

        this.cards.forEach((card) => {
          const video = card.querySelector('video');
          card.reelSources = readSources(video);
          card.querySelector('[data-reel-play]').addEventListener('click', () => this.onPlayClick(card));
          card.querySelector('[data-reel-mute]').addEventListener('click', () => this.onMuteClick(card));
          card.querySelector('[data-reel-media]').addEventListener('click', (event) => {
            if (!event.target.closest('button')) this.onPlayClick(card);
          });
          video.addEventListener('play', () => this.syncCard(card));
          video.addEventListener('pause', () => this.syncCard(card));
          video.addEventListener('timeupdate', () => this.syncTime(card));
          video.addEventListener('playing', () => card.classList.add('is-playing'), { once: true });
        });

        if (!this.scroller) return;
        const update = () => {
          const max = this.scroller.scrollWidth - this.scroller.clientWidth;
          const share = this.scroller.clientWidth / this.scroller.scrollWidth;
          const pos = max > 0 ? this.scroller.scrollLeft / max : 0;
          if (this.nav) this.nav.hidden = max <= 1;
          if (this.progress) {
            this.progress.style.width = `${Math.max(share, 0.1) * 100}%`;
            this.progress.style.transform = `translateX(${pos * (1 / Math.max(share, 0.1) - 1) * 100}%)`;
          }
          if (this.prev) this.prev.disabled = this.scroller.scrollLeft <= 1;
          if (this.next) this.next.disabled = this.scroller.scrollLeft >= max - 1;
        };
        const step = (direction) => {
          const card = this.cards[0];
          const by = Math.max((card ? card.offsetWidth : 250) + 32, this.scroller.clientWidth * 0.8);
          this.scroller.scrollBy({ left: direction * by, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        };
        if (this.prev) this.prev.addEventListener('click', () => step(-1));
        if (this.next) this.next.addEventListener('click', () => step(1));
        this.scroller.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        update();
      }

      disconnectedCallback() {
        if (this.sectionObserver) this.sectionObserver.disconnect();
        if (this.cardObserver) this.cardObserver.disconnect();
        this.sectionObserver = null;
        this.cardObserver = null;
      }

      ensureSource(card, userInitiated) {
        const video = card.querySelector('video');
        if (!video.getAttribute('src')) setSource(video, card.reelSources, userInitiated);
        return video;
      }

      pauseOthers(keep) {
        this.cards.forEach((card) => {
          if (card === keep) return;
          const video = card.querySelector('video');
          if (!video.paused) video.pause();
        });
      }

      // Muted autoplay of the most visible card, only while the reel is on screen.
      autoplay() {
        if (!this.inView) {
          this.pauseOthers(null);
          return;
        }
        if (noAutoplay() || this.userChoice) return;
        let best = null;
        let bestRatio = 0.6;
        this.cards.forEach((card) => {
          const ratio = this.visibility.get(card) || 0;
          if (ratio >= bestRatio && (!best || ratio > bestRatio)) {
            best = card;
            bestRatio = ratio;
          }
        });
        if (!best) return;
        this.pauseOthers(best);
        const video = this.ensureSource(best, false);
        if (video.getAttribute('src')) playQuietly(video);
      }

      onPlayClick(card) {
        const video = card.querySelector('video');
        if (video.paused) {
          this.userChoice = card;
          this.pauseOthers(card);
          playQuietly(this.ensureSource(card, true));
        } else {
          this.userChoice = card;
          video.pause();
        }
      }

      onMuteClick(card) {
        const video = card.querySelector('video');
        video.muted = !video.muted;
        if (!video.muted && video.paused) this.onPlayClick(card);
        this.syncCard(card);
      }

      syncCard(card) {
        const video = card.querySelector('video');
        const play = card.querySelector('[data-reel-play]');
        const mute = card.querySelector('[data-reel-mute]');
        const playing = !video.paused;
        card.classList.toggle('is-paused', !playing);
        play.setAttribute('aria-label', (playing ? play.dataset.labelPause : play.dataset.labelPlay) || '');
        mute.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
      }

      syncTime(card) {
        const video = card.querySelector('video');
        const bar = card.querySelector('[data-reel-time-bar]');
        const label = card.querySelector('[data-reel-time]');
        if (bar && video.duration) bar.style.width = `${(video.currentTime / video.duration) * 100}%`;
        if (label) {
          const s = Math.floor(video.currentTime);
          label.textContent = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
        }
      }
    }
    customElements.define('video-reel', VideoReel);
  }
})();
